import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setActiveCompanyId, getActiveCompanyId } from "./api";
import { useAuth } from "./auth";

const CompanyCtx = createContext(null);

export const CURRENCY_SYMBOLS = {
  INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "د.إ", AUD: "A$", CAD: "C$",
  SGD: "S$", JPY: "¥", CNY: "¥", ZAR: "R", BRL: "R$",
};

export function fmtMoney(amount, currency = "INR") {
  const sym = CURRENCY_SYMBOLS[currency] || `${currency} `;
  const n = Number(amount || 0);
  const str = n.toLocaleString(currency === "INR" ? "en-IN" : "en-US", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  return `${sym}${str}`;
}

export function CompanyProvider({ children }) {
  const { user, ready } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [activeId, setActiveId] = useState(getActiveCompanyId());
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) { setCompanies([]); return []; }
    setLoading(true);
    try {
      const { data } = await api.get("/companies");
      const list = data.companies || [];
      setCompanies(list);
      // Auto-select first company if nothing is active
      const stored = getActiveCompanyId();
      if (stored && list.find((c) => c.id === stored)) {
        setActiveId(stored);
      } else if (list.length > 0) {
        setActiveId(list[0].id);
        setActiveCompanyId(list[0].id);
      } else {
        setActiveId(null);
        setActiveCompanyId(null);
      }
      return list;
    } catch {
      return [];
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (ready && user) refresh();
  }, [ready, user, refresh]);

  const setActive = (id) => {
    setActiveId(id);
    setActiveCompanyId(id);
  };

  const active = companies.find((c) => c.id === activeId) || null;

  return (
    <CompanyCtx.Provider value={{ companies, active, activeId, setActive, refresh, loading }}>
      {children}
    </CompanyCtx.Provider>
  );
}

export function useCompany() {
  const ctx = useContext(CompanyCtx);
  if (!ctx) throw new Error("useCompany must be used within CompanyProvider");
  return ctx;
}
